/**
 * Admin: Resync ingredient brix from master_ingredients into existing
 * recipes. Two-phase flow — DRY-RUN PREVIEW first, then explicit
 * confirmation-phrase + apply. Strictly opt-in; never mutates anything
 * without the admin checking the confirmation box AND clicking apply.
 *
 * After a successful apply the page surfaces a summary banner and a
 * "Refetch preview" affordance so the admin can see the run-to-zero
 * effect in the same place.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { API } from '../App';

const EnvironmentBadge = ({ env }) => {
  if (!env) return null;
  const isPreview = env.is_preview;
  const cls = isPreview ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-green-100 text-green-800 border-green-300';
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-wider ${cls}`}
      data-testid="resync-env-badge"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${isPreview ? 'bg-amber-500' : 'bg-green-500'}`} />
      {isPreview ? 'PREVIEW' : 'PRODUCTION'}
      <span className="opacity-60 font-mono normal-case tracking-normal">{env.db_name}</span>
    </span>
  );
};

const SummaryTile = ({ label, value, cls, testid }) => (
  <div className={`rounded-lg p-3 ${cls}`} data-testid={testid}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

const fmt = (v) => (v === null || v === undefined ? <span className="text-gray-400">null</span> : <span className="tabular-nums">{Number(v).toFixed(2)}</span>);

const AdminResyncIngredientBrixPage = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [includeTestData, setIncludeTestData] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [search, setSearch] = useState('');

  // Pull the server's canonical phrase ONCE so the checkbox label can't
  // drift out of sync with the server-side check.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/resync-ingredient-brix/confirm-phrase`);
        setConfirmPhrase(r.data?.phrase || '');
      } catch (_e) {
        /* non-fatal — apply button will simply stay disabled */
      }
    })();
  }, []);

  const runPreview = async () => {
    setLoading(true);
    setApplyResult(null);
    setConfirmed(false);
    try {
      const r = await axios.get(`${API}/admin/resync-ingredient-brix/preview`, {
        params: {
          include_unpublished: includeUnpublished,
          include_test_data: includeTestData,
        },
      });
      setPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke køre dry-run');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!confirmed || !confirmPhrase) return;
    if (!preview || preview.summary.affected_row_count === 0) {
      toast.info('Ingen ændringer at anvende.');
      return;
    }
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/resync-ingredient-brix/apply`, {
        confirmation_phrase: confirmPhrase,
        include_unpublished: includeUnpublished,
        include_test_data: includeTestData,
      });
      setApplyResult(r.data);
      toast.success(
        `Synkroniseret ${r.data.summary.affected_row_count} ingrediensrækker i ${r.data.summary.affected_recipe_count} opskrifter.`
      );
      setConfirmed(false);
      // Refresh preview so the table now shows 0 changes (= clean state)
      await runPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke anvende ændringer');
    } finally {
      setApplying(false);
    }
  };

  const filteredChanges = useMemo(() => {
    if (!preview) return [];
    if (!search) return preview.changes;
    const q = search.toLowerCase();
    return preview.changes.filter(
      (c) =>
        (c.recipe_name || '').toLowerCase().includes(q) ||
        (c.ingredient_name || '').toLowerCase().includes(q)
    );
  }, [preview, search]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="admin-resync-brix-page">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Resync ingrediens-Brix til opskrifter</h1>
          <EnvironmentBadge env={preview?.environment} />
        </div>
        <p className="text-gray-600">
          Synkroniserer <code>ingredient.brix</code> på eksisterende opskrifters
          ingrediens-rækker fra <code>master_ingredients</code>-kataloget. Ingen
          ændring sker uden eksplicit bekræftelse. <strong>target_brix
          ændres ikke.</strong>
        </p>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <Link to="/admin/brix-outliers" className="text-blue-700 hover:underline">→ Brix-outliers rapport</Link>
          <Link to="/admin/ingredients-missing-brix" className="text-blue-700 hover:underline">→ Ingredienser uden Brix</Link>
        </div>
      </div>

      {/* Dataset toggles */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
        <span className="font-semibold text-blue-900">Datasæt:</span>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input type="checkbox" checked={includeTestData} onChange={(e) => setIncludeTestData(e.target.checked)} data-testid="resync-include-test" />
          Inkluder testdata
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input type="checkbox" checked={includeUnpublished} onChange={(e) => setIncludeUnpublished(e.target.checked)} data-testid="resync-include-unpublished" />
          Inkluder upublicerede
        </label>
        <button
          type="button"
          onClick={runPreview}
          disabled={loading}
          className="ml-auto px-4 py-1.5 bg-[#115DA8] hover:bg-[#0d4d8f] text-white rounded-md text-sm font-semibold disabled:opacity-50"
          data-testid="resync-run-preview"
        >
          {loading ? 'Beregner…' : (preview ? 'Genkør dry-run' : 'Kør dry-run preview')}
        </button>
      </div>

      {/* Apply summary banner */}
      {applyResult && (
        <div className="mb-4 rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-900" data-testid="resync-apply-summary">
          <div className="font-bold text-base mb-1">✅ Synkronisering gennemført</div>
          <p>
            Opdaterede <strong>{applyResult.summary.affected_row_count}</strong> ingrediensrækker
            i <strong>{applyResult.summary.affected_recipe_count}</strong> opskrifter.
            Tidsstempel: <code>{applyResult.applied_at}</code>.
          </p>
          <p className="mt-1 text-xs">
            Næste skridt: åbn <Link to="/admin/brix-outliers" className="underline">Brix-outliers</Link> og <Link to="/admin/ingredients-missing-brix" className="underline">Ingredienser uden Brix</Link> for at se effekten.
          </p>
        </div>
      )}

      {/* Preview state */}
      {!preview && !loading && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500" data-testid="resync-empty-state">
          Klik <strong>&quot;Kør dry-run preview&quot;</strong> ovenfor for at se hvilke ændringer der vil blive lavet. Ingen ændring sker før du bekræfter.
        </div>
      )}

      {preview && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="resync-summary">
            <SummaryTile label="Opskrifter påvirket" value={preview.summary.affected_recipe_count} cls="bg-blue-50 text-blue-800" testid="resync-summary-recipes" />
            <SummaryTile label="Ingrediensrækker" value={preview.summary.affected_row_count} cls="bg-green-50 text-green-800" testid="resync-summary-rows" />
            <SummaryTile label="Advarsler (ikke i katalog)" value={preview.summary.warnings_count} cls="bg-amber-50 text-amber-800" testid="resync-summary-warnings" />
            <SummaryTile label="Berørte advarsel-rækker" value={preview.summary.warnings_total_uses} cls="bg-orange-50 text-orange-800" testid="resync-summary-warning-uses" />
          </div>

          {/* Warnings */}
          {preview.warnings.length > 0 && (
            <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="resync-warnings">
              <div className="font-semibold text-amber-900 mb-2">
                ⚠️ {preview.warnings.length} ingredienser findes ikke i master_ingredients
              </div>
              <p className="text-xs text-amber-800 mb-3">
                Disse forbliver med deres nuværende værdi (typisk null/0). Tilføj dem via &quot;Opret i katalog&quot; på <Link to="/admin/ingredients-missing-brix" className="underline font-semibold">Ingredienser uden Brix</Link> rapporten først.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-amber-900">
                    <tr>
                      <th className="px-2 py-1">Ingrediens</th>
                      <th className="px-2 py-1 text-right">Brug</th>
                      <th className="px-2 py-1">Eksempler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.warnings.map((w, i) => (
                      <tr key={w.ingredient_name} className="border-t border-amber-200">
                        <td className="px-2 py-1 font-medium text-amber-900">{w.ingredient_name}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{w.recipe_uses}</td>
                        <td className="px-2 py-1 text-amber-800">
                          {w.sample_recipes.slice(0, 3).map((r) => r.name).join(', ')}
                          {w.recipe_uses > 3 && <span className="text-amber-600"> +{w.recipe_uses - 3}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Changes table */}
          <div className="bg-white rounded-lg border border-gray-200 mb-4">
            <div className="flex items-center gap-3 p-3 border-b border-gray-100">
              <span className="font-semibold text-gray-900">Planlagte ændringer</span>
              <input
                type="text"
                placeholder="Søg opskrift eller ingrediens…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm flex-1 max-w-md"
                data-testid="resync-search"
              />
              <span className="text-xs text-gray-500 ml-auto" data-testid="resync-filtered-count">
                {filteredChanges.length} af {preview.changes.length}
              </span>
            </div>
            <div className="overflow-x-auto max-h-[480px]">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-700 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Opskrift</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                    <th className="px-3 py-2 font-semibold">Ingrediens (matchet på)</th>
                    <th className="px-3 py-2 font-semibold text-right">Gammel</th>
                    <th className="px-3 py-2 font-semibold text-right">Ny</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredChanges.map((c) => (
                    <tr key={`${c.recipe_id}-${c.ingredient_index}`} className="hover:bg-gray-50" data-testid={`resync-row-${c.recipe_id}-${c.ingredient_index}`}>
                      <td className="px-3 py-2">
                        <Link to={`/recipes/${c.recipe_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                          {c.recipe_name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{c.recipe_type}</td>
                      <td className="px-3 py-2 text-gray-800">{c.ingredient_name}</td>
                      <td className="px-3 py-2 text-right">{fmt(c.old_brix)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">{fmt(c.new_brix)}</td>
                    </tr>
                  ))}
                  {filteredChanges.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                      {preview.changes.length === 0 ? 'Ingen ændringer påkrævet — kataloget og opskrifterne er allerede synkroniserede.' : 'Ingen rækker matcher søgningen.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Confirmation + apply */}
          {preview.summary.affected_row_count > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50/50 p-4" data-testid="resync-apply-section">
              <div className="font-bold text-red-900 mb-3">Bekræft og anvend</div>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-red-900 mb-3">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                  data-testid="resync-confirm-checkbox"
                />
                <span>
                  {confirmPhrase || 'Loader bekræftelsestekst…'}
                </span>
              </label>
              <button
                type="button"
                onClick={apply}
                disabled={!confirmed || applying || !confirmPhrase}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="resync-apply-btn"
              >
                {applying ? 'Anvender…' : `Anvend ${preview.summary.affected_row_count} ændringer på ${preview.summary.affected_recipe_count} opskrifter`}
              </button>
              <p className="text-xs text-red-800 mt-2">
                Operationen er ikke transaktionel — i tilfælde af fejl midtvejs kan nogle opskrifter være delvist opdaterede.
                Genkør dry-run efter applya for at verificere resultatet.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminResyncIngredientBrixPage;
